import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Illustration, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { DurationInMilliseconds } from 'constants/General'
import { useDeleteCryptoAddressMutation } from 'hooks/mutations/useDeleteCryptoAddressMutation'
import { BaseErrorResponse } from 'types/beneficiaries'
import { WhitelistingCryptoAddress } from 'types/whitelisting'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface DeleteAddressModalProps {
  handleOnModalClose: () => void
  selectedCryptoAddress: WhitelistingCryptoAddress
  onDeleteCallback: () => void
}

const DeleteAddressModal = ({
  handleOnModalClose,
  selectedCryptoAddress,
  onDeleteCallback,
}: DeleteAddressModalProps) => {
  const { t } = useTranslation()

  const { mutate: mutateDeleteAddress, isPending: isLoadingDeleteAddress } =
    useDeleteCryptoAddressMutation()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.DELETE_CRYPTO_ADDRESS_WHITELIST_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const handleOnDelete = async () => {
    const commonConfig = {
      onSuccess: () => {
        onDeleteCallback()
        toast({
          header: t('manageCryptoAddresses.modal.deleteSuccess'),
          variant: 'info',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        track.Amp.track(AmplitudeEvent.EOF_DELETE_CRYPTO_ADDRESS_WHITELIST_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
        })
      },
      onError: (error: BaseErrorResponse) => {
        logSentryError('Error from DeleteAddressModal - deleteCallback ', error)
        toast({
          header: t('oopsSomethingWentWrong'),
          variant: 'error',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        handleOnModalClose()
      },
    }
    mutateDeleteAddress({ cryptoId: selectedCryptoAddress?.uuid }, commonConfig)
  }

  return (
    <Dialog
      title={t('manageCryptoAddresses.modal.deleteTitle')}
      open
      onClose={handleOnModalClose}
      Illustration={<Illustration name="CloudLightning" />}
      ActionButtons={[
        <Button
          key="submit-button"
          testId="submit-button"
          onClick={handleOnDelete}
          variant="destructive"
          loading={isLoadingDeleteAddress}
        >
          {t('delete')}
        </Button>,
        <Button
          key="close-button"
          testId="close-button"
          onClick={handleOnModalClose}
          variant="secondary"
        >
          {t('cancel')}
        </Button>,
      ]}
    >
      <p className="text-foreground-prominent text-body-regular text-center">
        {t('manageCryptoAddresses.modal.deleteDescription')}
      </p>
    </Dialog>
  )
}

export default DeleteAddressModal
